import api from './api';

export interface BankAccount {
  id: number;
  account_number: string;
  bank_name: string | null;
  description: string | null;
  currency: string;
  is_primary: number;
  is_active: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

// Oczyść numer rachunku – usuń spacje i prefiks PL (IBAN)
function cleanAccountNumber(raw: string): string {
  let clean = raw.replace(/\s/g, '');
  if (clean.toUpperCase().startsWith('PL')) clean = clean.slice(2);
  return clean;
}

// Formatuj numer rachunku: xx xxxx xxxx xxxx xxxx xxxx xxxx (bez PL)
export function formatAccountNumber(raw: string): string {
  const clean = cleanAccountNumber(raw);
  if (clean.length !== 26) return raw;
  return `${clean.slice(0, 2)} ${clean.slice(2, 6)} ${clean.slice(6, 10)} ${clean.slice(10, 14)} ${clean.slice(14, 18)} ${clean.slice(18, 22)} ${clean.slice(22, 26)}`;
}

// Skrócony numer rachunku: xx xxxx...xxxx (np. 86 1140...1001)
export function shortenAccountNumber(raw: string): string {
  const clean = cleanAccountNumber(raw);
  if (clean.length < 10) return raw;
  return `${clean.slice(0, 2)} ${clean.slice(2, 6)}...${clean.slice(-4)}`;
}

// Responsywny format rachunku – zwraca { full, prefix, suffix }
// Pełny: xx xxxx xxxx xxxx xxxx xxxx xxxx
// Gdy kolumna wąska: prefix="xx xxxx xxxx xxxx xxxx xxxx" + ellipsis + suffix=" xxxx"
export function accountParts(raw: string): { full: string; prefix: string; suffix: string } {
  const clean = cleanAccountNumber(raw);
  if (clean.length < 10) return { full: raw, prefix: raw, suffix: '' };
  const full = formatAccountNumber(raw);
  // prefix = wszystko oprócz ostatnich 5 znaków (" xxxx"), suffix = " " + ostatnie 4 cyfry
  return { full, prefix: full.slice(0, -5), suffix: ' ' + clean.slice(-4) };
}

// Pobierz listę rachunków bankowych
export async function listBankAccounts(): Promise<BankAccount[]> {
  const { data } = await api.get('/bank-accounts');
  return data.items || [];
}

// Dodaj nowy rachunek bankowy
export async function createBankAccount(
  account_number: string,
  bank_name: string,
  description: string,
  currency: string,
  is_primary?: boolean,
): Promise<BankAccount> {
  const { data } = await api.post('/bank-accounts', { account_number, bank_name, description, currency, is_primary });
  return data.item;
}

// Aktualizuj rachunek bankowy
export async function updateBankAccount(
  id: number,
  account_number: string,
  bank_name: string,
  description: string,
  currency: string,
): Promise<BankAccount> {
  const { data } = await api.put(`/bank-accounts/${id}`, { account_number, bank_name, description, currency });
  return data.item;
}

// Ustaw rachunek jako główny
export async function setPrimaryBankAccount(id: number): Promise<BankAccount[]> {
  const { data } = await api.patch(`/bank-accounts/${id}/primary`);
  return data.items || [];
}

// Usuń rachunek bankowy
export async function deleteBankAccount(id: number): Promise<void> {
  await api.delete(`/bank-accounts/${id}`);
}
